import type { IGeometryShapeType } from '../../../domain/geometry-shape-types';

/**
 * The GeometrySelectListEvent is dispatched by the GeometrySelectList when the shape it shows selected moves, and when the user picks another shape for the window.
 *
 * It stands in for the SelectListEvent the SelectList raises, which the GeometrySelectList keeps to itself: a listener above the list hears about shapes, not about list items, and lists nested further down cannot be mistaken for this one.
 *
 * Two types, because they answer two questions. {@link GEOMETRY_CHANGED} is only for a shape the user picked: the list also selects shapes itself, to show what the position is cut to, and none of those is a change anyone asked for — see `GeometrySelectList`. {@link SHAPE_SELECTED} is for every shape the list shows
 * selected, whoever selected it, for whatever beside the list has to show the same shape.
 */
export default class GeometrySelectListEvent extends Event {
	/**
	 * The static GEOMETRY_CHANGED type is dispatched when the user picks a shape other than the one the position is cut to.
	 *
	 * Example: addEventListener(GeometrySelectListEvent.GEOMETRY_CHANGED, (event: GeometrySelectListEvent) => {});
	 */
	public static GEOMETRY_CHANGED = 'GeometrySelectListEvent.geometryChanged';

	/**
	 * The static SHAPE_SELECTED type is dispatched whenever the list comes to show another shape selected: picked by the user, or selected by the list to show what the position is cut to.
	 *
	 * Example: addEventListener(GeometrySelectListEvent.SHAPE_SELECTED, (event: GeometrySelectListEvent) => {});
	 */
	public static SHAPE_SELECTED = 'GeometrySelectListEvent.shapeSelected';

	/** The shape the event is about: the one the user picked, which is what a new configuration of the position is to be cut to, or the one the list now shows selected. */
	public get shapeType(): IGeometryShapeType {
		return this.#shapeType;
	}

	#shapeType: IGeometryShapeType;

	/** The type comes second and defaults to {@link GEOMETRY_CHANGED}, the one the machine answers, so a pick is made as it always was. */
	public constructor(shapeType: IGeometryShapeType, type: string = GeometrySelectListEvent.GEOMETRY_CHANGED) {
		super(type, { bubbles: true });
		this.#shapeType = shapeType;
	}
}
